import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { authService } from '../../services/apiService';
import api from '../../services/api';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: send OTP, 2: verify OTP
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: location.state?.email || '',
    otpCode: '',
    isEmailVerified: false
  });

  const handleGoogleVerification = useCallback(async (email) => {
    try {
      await api.post('/auth/verify-email-google', {
        collegeEmail: email
      });
      
      toast.success('Email verified with Google! You can now login.');
      setTimeout(() => {
        navigate('/login', { 
          state: { message: 'Email verified! You can now login.' }
        });
      }, 1500);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to verify email';
      
      // Handle specific error cases
      if (error.response?.status === 404) {
        toast.error('No account found. Redirecting to registration...');
        setTimeout(() => {
          navigate('/register', { state: { email } });
        }, 2000);
      } else if (errorMsg.includes('already verified')) {
        toast.info('Email already verified! Redirecting to login...');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        toast.error(errorMsg);
      }
      
      window.history.replaceState({}, '', '/verify-email');
    }
  }, [navigate]);

  // Check for Google OAuth verification
  useEffect(() => {
    const verifiedEmail = searchParams.get('verified_email');
    const error = searchParams.get('error');
    const errorMessage = searchParams.get('message');

    if (error) {
      toast.error(decodeURIComponent(errorMessage || 'Google verification failed'));
      window.history.replaceState({}, '', '/verify-email');
    } else if (verifiedEmail) {
      // Google verified - update database
      handleGoogleVerification(decodeURIComponent(verifiedEmail));
    }
  }, [searchParams, handleGoogleVerification]);

  const handleVerifyWithGoogle = async () => {
    try {
      const response = await api.get('/auth/google/login-url?state=email-verify');
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to initiate Google verification');
    }
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    // Validate college domain
    const validDomains = ['@cambridge.edu.in', '@citnc.ac.in'];
    const hasValidDomain = validDomains.some(domain => 
      formData.email.toLowerCase().endsWith(domain)
    );
    
    if (!hasValidDomain) {
      toast.error('Please use your college email address');
      return;
    }
    
    setLoading(true);

    try {
      await authService.resendVerification(formData.email);
      toast.success('Verification OTP sent to your email!');
      setStep(2);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Failed to send OTP';
      
      // Handle specific error cases
      if (error.response?.status === 404) {
        toast.error('No account found. Redirecting to registration...');
        setTimeout(() => {
          navigate('/register', { state: { email: formData.email } });
        }, 2000);
      } else if (errorMsg.includes('already verified')) {
        toast.info('Email already verified! Redirecting to login...');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    // Validate OTP format (should be 6 digits)
    if (!formData.otpCode || formData.otpCode.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }
    
    if (!/^\d{6}$/.test(formData.otpCode)) {
      toast.error('OTP should contain only numbers');
      return;
    }
    
    setLoading(true);

    try {
      const response = await authService.verifyEmail(formData.otpCode);
      toast.success(response.message || 'Email verified successfully!');
      
      setTimeout(() => {
        navigate('/login', { 
          state: { message: 'Email verified! You can now login.' }
        });
      }, 2000);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Invalid or expired OTP';
      
      if (errorMsg.includes('expired')) {
        toast.error('OTP has expired. Please request a new one.');
        // Optionally go back to step 1
        // setStep(1);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    try {
      await authService.resendVerification(formData.email);
      toast.success('OTP resent successfully!');
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <span className="text-3xl">📧</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900">Email Verification</h1>
          <p className="text-slate-600 mt-2">CITNC AIO</p>
        </div>

        {/* Verification Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Google OAuth - Primary Method */}
          <div className="mb-6">
            <button
              onClick={handleVerifyWithGoogle}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-white border-2 border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 hover:border-primary transition-all shadow-sm"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              <span>Verify with Google</span>
            </button>
            <p className="text-xs text-slate-500 text-center mt-2">
              Quick and secure verification using your college Google account
            </p>
          </div>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white text-slate-500">Or verify with OTP</span>
            </div>
          </div>

          {/* OTP Method - Secondary */}
          {step === 1 ? (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  College Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your.email.nc@cambridge.edu.in"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending OTP...
                  </span>
                ) : (
                  'Send Verification OTP'
                )}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Enter OTP Code
                </label>
                <input
                  type="text"
                  value={formData.otpCode}
                  onChange={(e) => setFormData({ ...formData, otpCode: e.target.value })}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-center text-2xl tracking-widest"
                  required
                />
                <p className="text-sm text-slate-600 mt-2">
                  OTP sent to <strong>{formData.email}</strong>
                </p>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Verifying...
                  </span>
                ) : (
                  'Verify Email'
                )}
              </button>
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="text-slate-600 hover:text-slate-900"
                >
                  ← Change Email
                </button>
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading}
                  className="text-primary hover:text-primary-dark font-medium disabled:opacity-50"
                >
                  Resend OTP
                </button>
              </div>
            </form>
          )}

          <Link
            to="/login"
            className="inline-block w-full text-center mt-6 text-primary hover:text-primary-dark font-medium"
          >
            ← Back to Login
          </Link>
        </div>

        {/* Help Text */}
        <div className="mt-6 text-center">
          <p className="text-sm text-slate-600">
            Need help? <Link to="/contact" className="text-primary hover:underline">Contact Support</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
